import type { WindowLevelConfig } from './types';
import type { Maybe } from '@/src/types';

import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import z from 'zod';

import {
  deleteSecondKey,
  doubleKeySchema,
  getDoubleKeyRecord,
  patchDoubleKeyRecord,
} from '@/src/utils/doubleKeyRecord';

import { WindowLevelConfigSchema } from './types';

export const defaultWindowLevelConfig = (): WindowLevelConfig => ({
  width: 1,
  level: 0.5,
  min: 0,
  max: 1,
});

export const SerializedWindowingStoreSchema = doubleKeySchema(WindowLevelConfigSchema);
export type SerializedWindowingStore = z.infer<typeof SerializedWindowingStoreSchema>;

const useWindowingStore = defineStore('windowing', () => {
  const configs = reactive<SerializedWindowingStore>({});
  const syncAcrossViews = ref(true);

  const setSyncAcrossViews = (yn: boolean) => {
    syncAcrossViews.value = yn;
  };

  const getConfig = (viewID: Maybe<string>, dataID: Maybe<string>) =>
    getDoubleKeyRecord(configs, viewID, dataID);

  /**
   * Syncs the window/level/min/max params of (srcViewID, srcDataID) across all views sharing the dataset.
   * @param srcViewID
   * @param srcDataID
   */
  const syncWindowLevel = (srcViewID: string, srcDataID: string) => {
    if (!syncAcrossViews.value) return;

    const config = configs[srcViewID]?.[srcDataID];
    if (!config) return;

    Object.keys(configs)
      .filter(viewID => viewID !== srcViewID)
      .forEach((viewID) => {
        patchDoubleKeyRecord(configs, viewID, srcDataID, config);
      });
  };

  const updateConfig = (
    viewID: string,
    dataID: string,
    patch: Partial<WindowLevelConfig>,
  ) => {
    patchDoubleKeyRecord(configs, viewID, dataID, {
      ...defaultWindowLevelConfig(),
      ...configs[viewID]?.[dataID],
      ...patch,
    });

    if (syncAcrossViews.value) {
      syncWindowLevel(viewID, dataID);
    }
  };

  // min/max reset
  const resetWindowLevel = (viewID: string, dataID: string) => {
    const config = configs[viewID]?.[dataID];
    if (config == null) return;

    const width = config.max - config.min;
    const level = (config.max + config.min) / 2;

    updateConfig(viewID, dataID, { width, level });
  };

  const removeView = (viewID: string) => {
    delete configs[viewID];
  };

  const removeData = (dataID: string, viewID?: string) => {
    if (viewID) {
      delete configs[viewID]?.[dataID];
    }
    else {
      deleteSecondKey(configs, dataID);
    }
  };

  const serialize = (): SerializedWindowingStore => JSON.parse(JSON.stringify(configs)) as SerializedWindowingStore; // configs is reactive; deep-clone to plain data

  const deserialize = (serialized?: SerializedWindowingStore) => {
    if (!serialized) return;
    Object.entries(serialized).forEach(([viewID, byData]) => {
      Object.entries(byData).forEach(([dataID, config]) => {
        patchDoubleKeyRecord(configs, viewID, dataID, {
          ...defaultWindowLevelConfig(),
          ...config,
        });
      });
    });
  };

  return {
    configs,
    getConfig,
    setSyncAcrossViews,
    updateConfig,
    resetWindowLevel,
    removeView,
    removeData,
    serialize,
    deserialize,
  };
});

export default useWindowingStore;
